import { useMemo } from 'react';
import type { Row } from 'write-excel-file';

import type { WidgetExportOptions } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import type { AggregateList } from 'src/components/followUp/common/types';

type UseExportOptionsProps = {
  data: AggregateList | null;
  comparisonData: AggregateList | null;
  labels: { name: string }[];
} & Pick<WidgetExportOptions, 'widgetName' | 'queryFilters'>;

const mapRows = (
  data: AggregateList | null,
  labels: { name: string }[],
): Row[] => {
  if (!data) return [];
  const valueKeys = ['open', 'inProgress', 'closed', 'overDue'];

  return valueKeys.map<Row>((valueKey, index) => {
    const value =
      data?.results.find((result) => result.label === valueKey)?.value ?? 0;
    const relativeValue = getRelativeValue(value, data?.totalObjects ?? 0, 0);

    return [
      {
        value: labels[index].name,
        fontWeight: 'bold',
      },
      {
        value,
      },
      {
        value: `${relativeValue}%`,
        align: 'right',
      },
    ];
  });
};

export const useExportOptions = ({
  data,
  comparisonData,
  labels,
  widgetName,
  queryFilters,
}: UseExportOptionsProps): WidgetExportOptions =>
  useMemo(() => {
    const rows = mapRows(data, labels);
    const comparisonRows = comparisonData
      ? mapRows(comparisonData, labels)
      : undefined;

    return {
      widgetName: widgetName,
      rows,
      comparisonRows,
      queryFilters,
    };
  }, [data, comparisonData, labels, widgetName, queryFilters]);
